import React, {
  useEffect,
  useState,
  useContext,
  useCallback,
  lazy,
  Suspense,
} from "react";
import secureAxios from "../../utils/secureAxios";
import { getApiBaseURL } from "../../utils/api";
import { AuthContext } from "../../contexts/AuthContext";
import { useAuditContext } from "../../contexts/AuditContext";
import { toast } from "react-toastify";
import FixInvoiceModal from "./FixInvoiceModal";

// Lazy load enhancement components
const InvoiceAgingReport = lazy(() => import("./InvoiceAgingReport"));
const RecordPaymentModal = lazy(() => import("./RecordPaymentModal"));
const InvoiceDetailModal = lazy(() => import("./InvoiceDetailModal"));
const MilestoneInvoiceGenerator = lazy(() => import("./MilestoneInvoiceGenerator"));
const InvoiceLookup = lazy(() => import("./InvoiceLookup"));

/**
 * PostedInvoicesPage
 *
 * Displays all invoices that have been generated — every record in the invoice
 * table regardless of status (pending, generated, sent, partially_paid, paid, void).
 *
 * The defining cut point:
 *   - Invoice Prep  (/crm/invoices)         = pre-generation workflow, nothing in DB yet
 *   - Posted Invoices (/crm/posted-invoices) = any invoice record that exists in the DB
 *
 * Tabs:
 *   - Posted Invoices : searchable/filterable invoice list with actions
 *   - Invoice Lookup  : search by invoice number or UUID prefix
 *   - Aging Report    : outstanding invoices by bucket (enhancements only)
 *   - Milestone Billing: flat-rate milestone invoicing (enhancements only)
 *
 * Route: /crm/posted-invoices
 */
const PostedInvoicesPage = () => {
  const [activeTab, setActiveTab] = useState("list");
  const [authFilter, setAuthFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [logTypeFilter, setLogTypeFilter] = useState("");
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(false);
  const [undoingInvoice, setUndoingInvoice] = useState(null);

  // Payment modal state
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Detail modal state
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailInvoice, setDetailInvoice] = useState(null);

  // Fix invoice modal state
  const [showFixModal, setShowFixModal] = useState(false);
  const [fixInvoice, setFixInvoice] = useState(null);

  const apiBase = getApiBaseURL();
  const { tenant_id, user } = useContext(AuthContext);
  const { logAuditEvent, PURPOSES } = useAuditContext();

  const invoiceEnhancementsEnabled = user?.enable_invoices === true;

  // ============================================================
  // Data fetching
  // ============================================================

  const fetchInvoices = useCallback(async () => {
    if (!tenant_id) return;
    setLoading(true);
    try {
      secureAxios.defaults.headers.common["X-Audit-Reason"] = "View posted invoices";
      secureAxios.defaults.headers.common["X-Audit-Purpose"] = PURPOSES.PAYMENT;

      const params = {
        tenant_id,
        auth_number: authFilter || undefined,
        status: statusFilter || undefined,
        log_type: logTypeFilter || undefined,
      };

      const res = await secureAxios.get(`${apiBase}/api/invoices/list`, { params });
      setInvoices(res.data);

      await logAuditEvent({
        action: "VIEW_POSTED_INVOICE_LIST",
        entity_type: "INVOICE",
        entity_id: null,
        entity_identifier: authFilter ? `Filtered by auth: ${authFilter}` : "All posted invoices",
        contains_phi: true,
        phi_types: ["authorization_number", "consumer_name"],
        purpose_of_use: PURPOSES.PAYMENT,
        action_status: "SUCCESS",
      });
    } catch (err) {
      console.error("[ERROR] Error fetching posted invoices:", err);
      toast.error("Failed to load invoices");

      await logAuditEvent({
        action: "VIEW_POSTED_INVOICE_LIST_FAILED",
        entity_type: "INVOICE",
        entity_id: null,
        action_status: "FAILED",
        action_reason: err.message,
      });
    } finally {
      setLoading(false);
      delete secureAxios.defaults.headers.common["X-Audit-Reason"];
      delete secureAxios.defaults.headers.common["X-Audit-Purpose"];
    }
  }, [tenant_id, authFilter, statusFilter, logTypeFilter, apiBase, logAuditEvent, PURPOSES]);

  useEffect(() => {
    if (activeTab === "list" && tenant_id) {
      fetchInvoices();
    }
  }, [activeTab, tenant_id, fetchInvoices]);

  // ============================================================
  // Tab navigation
  // ============================================================

  const handleTabChange = async (newTab) => {
    if (
      (newTab === "aging" || newTab === "milestone") &&
      !invoiceEnhancementsEnabled
    ) {
      return;
    }
    setActiveTab(newTab);
    await logAuditEvent({
      action: "NAVIGATE_POSTED_INVOICES_TAB",
      entity_type: "INVOICE",
      entity_id: null,
      entity_identifier: `Navigate to ${newTab} tab`,
      contains_phi: false,
      action_status: "SUCCESS",
    });
  };

  // ============================================================
  // Action handlers
  // ============================================================

  const handleViewPDF = async (invoice) => {
    try {
      secureAxios.defaults.headers.common["X-Audit-Reason"] =
        `View invoice PDF for ${invoice.consumer_name}`;
      secureAxios.defaults.headers.common["X-Audit-Purpose"] = PURPOSES.PAYMENT;

      await logAuditEvent({
        action: "VIEW_INVOICE_PDF",
        entity_type: "INVOICE",
        entity_id: invoice.invoice_id,
        entity_identifier: `Invoice for ${invoice.consumer_name} - Auth: ${invoice.authorization_number}`,
        contains_phi: true,
        phi_types: ["consumer_name", "authorization_number", "case_id", "service_details"],
        purpose_of_use: PURPOSES.PAYMENT,
        action_status: "SUCCESS",
      });

      window.open(`${apiBase}/api/invoices/${invoice.invoice_id}/pdf`, "_blank");
    } catch (err) {
      console.error("[ERROR] Error viewing PDF:", err);
      toast.error("Failed to open invoice PDF");
    } finally {
      delete secureAxios.defaults.headers.common["X-Audit-Reason"];
      delete secureAxios.defaults.headers.common["X-Audit-Purpose"];
    }
  };

  const handleUndoInvoice = async (invoice) => {
    if (invoice.amount_paid > 0) {
      toast.error("Cannot undo an invoice that has payments recorded. Void the invoice instead.");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to undo this invoice for ${invoice.consumer_name}? ` +
          `This will delete the invoice and make all associated logs available for re-invoicing.`
      )
    ) {
      return;
    }

    setUndoingInvoice(invoice.invoice_id);
    try {
      secureAxios.defaults.headers.common["X-Audit-Reason"] =
        `Undo invoice for corrections - ${invoice.consumer_name}`;
      secureAxios.defaults.headers.common["X-Audit-Purpose"] = PURPOSES.PAYMENT;

      const response = await secureAxios.delete(
        `${apiBase}/api/invoices/undo/${invoice.invoice_id}`
      );

      if (response.data.success) {
        toast.success("Invoice undone. Logs are now available for re-invoicing.");

        await logAuditEvent({
          action: "UNDO_INVOICE_SUCCESS",
          entity_type: "INVOICE",
          entity_id: invoice.invoice_id,
          entity_identifier: `Undone invoice for ${invoice.consumer_name} - Auth: ${invoice.authorization_number}`,
          contains_phi: true,
          phi_types: ["consumer_name", "authorization_number"],
          purpose_of_use: PURPOSES.PAYMENT,
          action_status: "SUCCESS",
        });

        await fetchInvoices();
      }
    } catch (err) {
      console.error("[ERROR] Error undoing invoice:", err);
      toast.error("Failed to undo invoice. Please try again.");

      await logAuditEvent({
        action: "UNDO_INVOICE_FAILED",
        entity_type: "INVOICE",
        entity_id: invoice.invoice_id,
        action_status: "FAILED",
        action_reason: err.message,
      });
    } finally {
      setUndoingInvoice(null);
      delete secureAxios.defaults.headers.common["X-Audit-Reason"];
      delete secureAxios.defaults.headers.common["X-Audit-Purpose"];
    }
  };

  const handleVoidInvoice = async (invoice) => {
    if (invoice.amount_paid > 0) {
      toast.error("Cannot void an invoice that has payments recorded.");
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to VOID invoice ${
          invoice.invoice_number || invoice.invoice_id
        }? This action cannot be undone.`
      )
    ) {
      return;
    }

    try {
      secureAxios.defaults.headers.common["X-Audit-Reason"] =
        `Void invoice - ${invoice.consumer_name}`;
      secureAxios.defaults.headers.common["X-Audit-Purpose"] = PURPOSES.PAYMENT;

      await secureAxios.post(`${apiBase}/api/invoices/${invoice.invoice_id}/void`, {
        tenant_id,
        void_reason: "User requested void",
      });

      toast.success(`Invoice ${invoice.invoice_number || ""} has been voided`);

      await logAuditEvent({
        action: "VOID_INVOICE_SUCCESS",
        entity_type: "INVOICE",
        entity_id: invoice.invoice_id,
        entity_identifier: `Voided invoice for ${invoice.consumer_name}`,
        contains_phi: true,
        phi_types: ["consumer_name", "authorization_number"],
        purpose_of_use: PURPOSES.PAYMENT,
        action_status: "SUCCESS",
      });

      await fetchInvoices();
    } catch (err) {
      console.error("[ERROR] Error voiding invoice:", err);
      toast.error(err.response?.data?.error || "Failed to void invoice");

      await logAuditEvent({
        action: "VOID_INVOICE_FAILED",
        entity_type: "INVOICE",
        entity_id: invoice.invoice_id,
        action_status: "FAILED",
        action_reason: err.message,
      });
    } finally {
      delete secureAxios.defaults.headers.common["X-Audit-Reason"];
      delete secureAxios.defaults.headers.common["X-Audit-Purpose"];
    }
  };

  const handleStatusUpdate = async (invoice, newStatus) => {
    try {
      secureAxios.defaults.headers.common["X-Audit-Reason"] =
        `Update invoice status to ${newStatus}`;
      secureAxios.defaults.headers.common["X-Audit-Purpose"] = PURPOSES.PAYMENT;

      await secureAxios.patch(
        `${apiBase}/api/invoices/${invoice.invoice_id}/status`,
        { tenant_id, status: newStatus }
      );

      toast.success(`Invoice marked as ${newStatus}`);

      await logAuditEvent({
        action: "UPDATE_INVOICE_STATUS",
        entity_type: "INVOICE",
        entity_id: invoice.invoice_id,
        entity_identifier: `Status changed to ${newStatus} for ${invoice.consumer_name}`,
        contains_phi: true,
        phi_types: ["consumer_name"],
        purpose_of_use: PURPOSES.PAYMENT,
        action_status: "SUCCESS",
      });

      await fetchInvoices();
    } catch (err) {
      console.error("[ERROR] Error updating status:", err);
      toast.error("Failed to update invoice status");
    } finally {
      delete secureAxios.defaults.headers.common["X-Audit-Reason"];
      delete secureAxios.defaults.headers.common["X-Audit-Purpose"];
    }
  };

  const openPaymentModal = (invoice) => {
    setSelectedInvoice(invoice);
    setShowPaymentModal(true);
  };

  const openDetailModal = (invoice) => {
    setDetailInvoice(invoice);
    setShowDetailModal(true);
  };

  const openFixModal = (invoice) => {
    setFixInvoice(invoice);
    setShowFixModal(true);
  };

  const handlePaymentRecorded = async (paymentData) => {
    await logAuditEvent({
      action: "RECORD_PAYMENT_SUCCESS",
      entity_type: "INVOICE",
      entity_id: paymentData.invoice_id,
      entity_identifier: `Payment of $${paymentData.amount} recorded`,
      contains_phi: true,
      phi_types: ["consumer_name", "payment_amount"],
      purpose_of_use: PURPOSES.PAYMENT,
      action_status: "SUCCESS",
    });

    setShowPaymentModal(false);
    setSelectedInvoice(null);
    await fetchInvoices();
  };

  // ============================================================
  // Helpers
  // ============================================================

  const formatCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    const rounded = Math.round(Math.round(num * 1000) / 10) / 100;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(rounded);
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: "bg-warning text-dark",
      generated: "bg-secondary",
      sent: "bg-primary",
      partially_paid: "bg-info",
      paid: "bg-success",
      void: "bg-dark",
    };
    return badges[status] || "bg-secondary";
  };

  const canModifyInvoice = (invoice) =>
    invoice.status !== "void" && invoice.status !== "paid";

  // ============================================================
  // Render
  // ============================================================

  return (
    <div className="container-fluid mt-4">
      {/* HIPAA Notice */}
      <div className="alert alert-info mb-4 py-2">
        <i className="bi bi-shield-lock me-2"></i>
        <strong>HIPAA Notice:</strong> All invoice activities are logged for
        compliance. Access only the information necessary for your authorized
        business purposes.
      </div>

      {/* Tab Navigation */}
      <ul className="nav nav-tabs mb-3">
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "list" ? "active" : ""}`}
            onClick={() => handleTabChange("list")}
          >
            <i className="bi bi-list-ul me-1"></i>
            Posted Invoices
          </button>
        </li>
        <li className="nav-item">
          <button
            className={`nav-link ${activeTab === "lookup" ? "active" : ""}`}
            onClick={() => handleTabChange("lookup")}
          >
            <i className="bi bi-search me-1"></i>
            Invoice Lookup
          </button>
        </li>
        {invoiceEnhancementsEnabled && (
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "aging" ? "active" : ""}`}
              onClick={() => handleTabChange("aging")}
            >
              <i className="bi bi-clock-history me-1"></i>
              Aging Report
            </button>
          </li>
        )}
        {invoiceEnhancementsEnabled && (
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === "milestone" ? "active" : ""}`}
              onClick={() => handleTabChange("milestone")}
            >
              <i className="bi bi-list-check me-1"></i>
              Milestone Billing
            </button>
          </li>
        )}
      </ul>

      {/* ======================================================
          TAB: Posted Invoices List
      ====================================================== */}
      {activeTab === "list" && (
        <div>
          {/* Filters */}
          <div className="card mb-3">
            <div className="card-body">
              <div className="row g-2">
                <div className="col-md-4">
                  <label className="form-label small">Authorization #</label>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Filter by authorization number"
                    value={authFilter}
                    onChange={(e) => setAuthFilter(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && fetchInvoices()}
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label small">Status</label>
                  <select
                    className="form-select form-select-sm"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">All Statuses</option>
                    <option value="pending">Pending</option>
                    <option value="generated">Generated</option>
                    <option value="sent">Sent</option>
                    <option value="partially_paid">Partially Paid</option>
                    <option value="paid">Paid</option>
                    <option value="void">Void</option>
                  </select>
                </div>
                <div className="col-md-2">
                  <label className="form-label small">Log Type</label>
                  <select
                    className="form-select form-select-sm"
                    value={logTypeFilter}
                    onChange={(e) => setLogTypeFilter(e.target.value)}
                  >
                    <option value="">All Types</option>
                    <option value="job">Job Logs</option>
                    <option value="mileage">Mileage</option>
                  </select>
                </div>
                <div className="col-md-2 d-flex align-items-end gap-2">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={fetchInvoices}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-1" />
                        Loading...
                      </>
                    ) : (
                      <>
                        <i className="bi bi-search me-1"></i>
                        Search
                      </>
                    )}
                  </button>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    onClick={() => {
                      setAuthFilter("");
                      setStatusFilter("");
                      setLogTypeFilter("");
                    }}
                  >
                    Clear
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Invoice Table */}
          {loading ? (
            <div className="text-center p-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : invoices.length === 0 ? (
            <div className="alert alert-info">
              <i className="bi bi-inbox me-2"></i>
              No invoices found. Try adjusting your filter criteria.
            </div>
          ) : (
            <div className="table-responsive">
              <table className="table table-bordered table-hover">
                <thead className="table-dark">
                  <tr>
                    <th>Invoice #</th>
                    <th>Authorization #</th>
                    <th>Consumer</th>
                    <th>Job Coach</th>
                    <th>Due Date</th>
                    <th className="text-end">Total</th>
                    {invoiceEnhancementsEnabled && (
                      <th className="text-end">Paid</th>
                    )}
                    {invoiceEnhancementsEnabled && (
                      <th className="text-end">Balance</th>
                    )}
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => {
                    const balanceDue = inv.balance_due ?? inv.total_amount;
                    const amountPaid = parseFloat(inv.amount_paid) || 0;
                    const isOverdue =
                      inv.due_date &&
                      new Date(inv.due_date) < new Date() &&
                      inv.status !== "paid" &&
                      inv.status !== "void";

                    return (
                      <tr
                        key={inv.invoice_id}
                        className={inv.status === "void" ? "table-secondary" : ""}
                        style={invoiceEnhancementsEnabled ? { cursor: "pointer" } : {}}
                        onClick={
                          invoiceEnhancementsEnabled
                            ? () => openDetailModal(inv)
                            : undefined
                        }
                      >
                        <td>
                          <span className="fw-bold text-primary">
                            {inv.invoice_number || (
                              <code className="small text-muted">
                                {inv.invoice_id?.slice(0, 8)}
                              </code>
                            )}
                          </span>
                        </td>
                        <td>{inv.authorization_number}</td>
                        <td>
                          <i className="bi bi-person-fill me-1 text-muted"></i>
                          {inv.consumer_name}
                        </td>
                        <td>{inv.user_name}</td>
                        <td>
                          {inv.due_date ? (
                            <span className={isOverdue ? "text-danger fw-bold" : ""}>
                              {new Date(inv.due_date).toLocaleDateString()}
                              {isOverdue && (
                                <i
                                  className="bi bi-exclamation-triangle ms-1"
                                  title="Overdue"
                                ></i>
                              )}
                            </span>
                          ) : (
                            <span className="text-muted"> -- </span>
                          )}
                        </td>
                        <td className="text-end">
                          {formatCurrency(inv.total_amount)}
                          {inv.flat_rate && (
                            <span
                              className="badge bg-success ms-1"
                              style={{ fontSize: "0.65em" }}
                              title="Flat Rate Service"
                            >
                              Flat
                            </span>
                          )}
                        </td>
                        {invoiceEnhancementsEnabled && (
                          <td className="text-end text-success">
                            {formatCurrency(amountPaid)}
                          </td>
                        )}
                        {invoiceEnhancementsEnabled && (
                          <td className="text-end">
                            <span
                              className={
                                balanceDue > 0 && inv.status !== "void"
                                  ? "fw-bold text-danger"
                                  : ""
                              }
                            >
                              {formatCurrency(balanceDue)}
                            </span>
                          </td>
                        )}
                        <td>
                          <span
                            className={`badge ${getStatusBadge(inv.status)}`}
                          >
                            {(inv.status || "pending")
                              .replace("_", " ")
                              .toUpperCase()}
                          </span>
                        </td>
                        <td onClick={(e) => e.stopPropagation()}>
                          <div className="dropdown">
                            <button
                              className="btn btn-sm btn-outline-secondary dropdown-toggle"
                              type="button"
                              data-bs-toggle="dropdown"
                              data-bs-boundary="viewport"
                              data-bs-auto-close="true"
                              aria-expanded="false"
                            >
                              <i className="bi bi-three-dots"></i>
                            </button>
                            <ul className="dropdown-menu dropdown-menu-end">

                              {/* View Details */}
                              {invoiceEnhancementsEnabled && (
                                <li>
                                  <button
                                    className="dropdown-item"
                                    onClick={() => openDetailModal(inv)}
                                  >
                                    <i className="bi bi-eye me-2"></i>
                                    View Details
                                  </button>
                                </li>
                              )}

                              {/* View PDF */}
                              <li>
                                <button
                                  className="dropdown-item"
                                  onClick={() => handleViewPDF(inv)}
                                >
                                  <i className="bi bi-file-pdf me-2"></i>
                                  View PDF
                                </button>
                              </li>

                              {/* Fix Posted Invoice - admin only, always available */}
                              {user?.role === "admin" && (
                                <li>
                                  <button
                                    className="dropdown-item text-primary"
                                    onClick={() => openFixModal(inv)}
                                    title="Correct line item amounts and recalculate invoice total"
                                  >
                                    <i className="bi bi-pencil-square me-2"></i>
                                    Fix Posted Invoice
                                  </button>
                                </li>
                              )}

                              {/* Record Payment */}
                              {invoiceEnhancementsEnabled &&
                                balanceDue > 0 &&
                                inv.status !== "void" &&
                                user?.role === "admin" && (
                                  <li>
                                    <button
                                      className="dropdown-item"
                                      onClick={() => openPaymentModal(inv)}
                                    >
                                      <i className="bi bi-cash-coin me-2"></i>
                                      Record Payment
                                    </button>
                                  </li>
                                )}

                              {/* Mark as Sent */}
                              {invoiceEnhancementsEnabled &&
                                (inv.status === "generated" ||
                                  inv.status === "pending") &&
                                user?.role === "admin" && (
                                  <li>
                                    <button
                                      className="dropdown-item"
                                      onClick={() =>
                                        handleStatusUpdate(inv, "sent")
                                      }
                                    >
                                      <i className="bi bi-send me-2"></i>
                                      Mark as Sent
                                    </button>
                                  </li>
                                )}

                              <li>
                                <hr className="dropdown-divider" />
                              </li>

                              {/* Undo Invoice */}
                              {user?.role === "admin" &&
                                canModifyInvoice(inv) &&
                                amountPaid === 0 && (
                                  <li>
                                    <button
                                      className="dropdown-item text-warning"
                                      onClick={() => handleUndoInvoice(inv)}
                                      disabled={undoingInvoice === inv.invoice_id}
                                      title="Deletes the invoice and releases logs for re-invoicing. Use to fix mistakes before sending."
                                    >
                                      {undoingInvoice === inv.invoice_id ? (
                                        <>
                                          <span className="spinner-border spinner-border-sm me-2" />
                                          Undoing...
                                        </>
                                      ) : (
                                        <>
                                          <i className="bi bi-arrow-counterclockwise me-2"></i>
                                          Undo Invoice
                                        </>
                                      )}
                                    </button>
                                  </li>
                                )}

                              {/* Void Invoice */}
                              {invoiceEnhancementsEnabled &&
                                user?.role === "admin" &&
                                canModifyInvoice(inv) &&
                                amountPaid === 0 && (
                                  <li>
                                    <button
                                      className="dropdown-item text-danger"
                                      onClick={() => handleVoidInvoice(inv)}
                                      title="Marks invoice as void for audit trail. Logs remain attached. Use for invoices already sent to customers."
                                    >
                                      <i className="bi bi-x-circle me-2"></i>
                                      Void Invoice
                                    </button>
                                  </li>
                                )}

                            </ul>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr>
                    <td
                      colSpan={invoiceEnhancementsEnabled ? "10" : "8"}
                      className="text-muted text-center small"
                    >
                      <i className="bi bi-info-circle me-1"></i>
                      Showing {invoices.length} invoice(s). All access is logged
                      for HIPAA compliance.
                      {user?.role === "admin" && (
                        <span className="ms-2">
                          Admin users can manage invoices and record payments.
                        </span>
                      )}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ======================================================
          TAB: Invoice Lookup
      ====================================================== */}
      {activeTab === "lookup" && (
        <Suspense
          fallback={
            <div className="text-center p-5">
              <span className="spinner-border"></span>
              <p className="mt-2">Loading Invoice Lookup...</p>
            </div>
          }
        >
          <InvoiceLookup
            onViewDetail={invoiceEnhancementsEnabled ? openDetailModal : undefined}
          />
        </Suspense>
      )}

      {/* ======================================================
          TAB: Aging Report (enhancements only)
      ====================================================== */}
      {activeTab === "aging" && invoiceEnhancementsEnabled && (
        <Suspense
          fallback={
            <div className="text-center p-5">
              <span className="spinner-border"></span>
              <p className="mt-2">Loading Aging Report...</p>
            </div>
          }
        >
          <InvoiceAgingReport />
        </Suspense>
      )}

      {/* ======================================================
          TAB: Milestone Billing (enhancements only)
      ====================================================== */}
      {activeTab === "milestone" && invoiceEnhancementsEnabled && (
        <Suspense
          fallback={
            <div className="text-center p-5">
              <span className="spinner-border"></span>
              <p className="mt-2">Loading Milestone Billing...</p>
            </div>
          }
        >
          <MilestoneInvoiceGenerator onInvoicesGenerated={fetchInvoices} />
        </Suspense>
      )}

      {/* ======================================================
          Modals (enhancements only)
      ====================================================== */}
      {invoiceEnhancementsEnabled && (
        <Suspense fallback={null}>
          <RecordPaymentModal
            show={showPaymentModal}
            onClose={() => {
              setShowPaymentModal(false);
              setSelectedInvoice(null);
            }}
            onPaymentRecorded={handlePaymentRecorded}
            invoice={selectedInvoice}
          />
        </Suspense>
      )}

      {invoiceEnhancementsEnabled && (
        <Suspense fallback={null}>
          <InvoiceDetailModal
            show={showDetailModal}
            onClose={() => {
              setShowDetailModal(false);
              setDetailInvoice(null);
            }}
            invoice={detailInvoice}
            onUpdate={fetchInvoices}
          />
        </Suspense>
      )}

      {/* Fix Invoice Modal - admin only, no feature flag required */}
      <FixInvoiceModal
        show={showFixModal}
        invoice={fixInvoice}
        onClose={() => {
          setShowFixModal(false);
          setFixInvoice(null);
        }}
        onSaved={fetchInvoices}
      />
    </div>
  );
};

export default PostedInvoicesPage;